import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Zap, Info, Moon, Sun, X, RefreshCw, Copy, FileText, Languages, Settings, Wand2, Sparkles } from "lucide-react";
import { createWorker } from "tesseract.js";
import ToolsNav from "../components/ToolsNav";
import { RenderCMSContent } from "../components/CMSRenderer";
import { AdPlacement, AdContainer, ResponsiveAd, StickyMobileAd, MobileOnlyAd, DesktopOnlyAd } from "../components/AdPlacement";

function ImageToText({ embedded = false }) {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [language, setLanguage] = useState("eng");
  const [enhanceImage, setEnhanceImage] = useState(true);
  const [useAiOcr, setUseAiOcr] = useState(true); // AI OCR mode - default ON for best accuracy
  const [pageSegMode, setPageSegMode] = useState("3"); // PSM_AUTO
  const [pageContent, setPageContent] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const workerRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const ACCEPT_TYPES = "image/png,image/jpeg,image/jpg,image/webp,image/bmp,image/gif,.png,.jpg,.jpeg,.webp,.bmp,.gif";

  const languages = [
    { code: "eng", name: "English" },
    { code: "spa", name: "Spanish" },
    { code: "fra", name: "French" },
    { code: "deu", name: "German" },
    { code: "ita", name: "Italian" },
    { code: "por", name: "Portuguese" },
    { code: "rus", name: "Russian" },
    { code: "chi_sim", name: "Chinese (Simplified)" },
    { code: "chi_tra", name: "Chinese (Traditional)" },
    { code: "jpn", name: "Japanese" },
    { code: "kor", name: "Korean" },
    { code: "ara", name: "Arabic" },
    { code: "hin", name: "Hindi" },
  ];

  const pageSegModes = [
    { value: "1", name: "Auto with OSD", desc: "Automatic with orientation detection" },
    { value: "3", name: "Auto (Default)", desc: "Fully automatic page segmentation" },
    { value: "4", name: "Single Column", desc: "Single column of variable sizes" },
    { value: "6", name: "Single Block", desc: "Uniform block of text" },
    { value: "7", name: "Single Line", desc: "Single text line" },
    { value: "8", name: "Single Word", desc: "Single word" },
    { value: "11", name: "Sparse Text", desc: "Find as much text as possible" },
    { value: "12", name: "Sparse + OSD", desc: "Sparse text with OSD" },
  ];

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/image-to-text`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
          document.title = data.seo?.meta_title || "Image to Text (OCR) - KayTool";
        }
      } catch (error) {
        document.title = "Image to Text (OCR) - KayTool";
      }
    };
    
    if (!embedded) {
      fetchPageContent();
    }

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, [embedded]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    if (files.length > 0) {
      processImages(files);
    } else {
      toast.error("Please drop image files only");
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      processImages(files);
    }
    e.target.value = '';
  };

  // Preprocess: Extract bright/white text from any background
  // Works well for white text on colorful/gradient backgrounds
  const preprocessWhiteText = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Reduced scale for faster processing (2x or 2000px max)
        const scale = Math.min(2, 2000 / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // For white/bright text: Isolate pixels where all RGB channels are high
        // This extracts white text while removing colorful backgrounds
        // Using lower threshold to capture more of the text including shadows
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i + 1], b = data[i + 2];
          
          // Calculate brightness and how "white" the pixel is
          const brightness = (r + g + b) / 3;
          const minChannel = Math.min(r, g, b);
          const maxChannel = Math.max(r, g, b);
          const saturation = maxChannel > 0 ? (maxChannel - minChannel) / maxChannel : 0;
          
          // White text has high brightness and LOW saturation (all channels similar)
          // Lower threshold to capture more text (brightness > 160, saturation < 0.4)
          const isWhiteText = brightness > 155 && saturation < 0.4;
          
          if (isWhiteText) {
            // Make text black on white background (Tesseract prefers this)
            data[i] = 0;
            data[i + 1] = 0;
            data[i + 2] = 0;
          } else {
            // Make background white
            data[i] = 255;
            data[i + 1] = 255;
            data[i + 2] = 255;
          }
        }
        
        ctx.putImageData(imageData, 0, 0);
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png', 1.0);
        
        URL.revokeObjectURL(img.src);
      };
      
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  // Preprocess: Simple high-contrast grayscale (good for standard documents)
  const preprocessGrayscale = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Reduced scale for faster processing (2x or 2000px max)
        const scale = Math.min(2, 2000 / Math.max(img.width, img.height));
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Convert to grayscale with moderate contrast
        for (let i = 0; i < data.length; i += 4) {
          const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          const contrast = 1.4;
          const factor = (259 * (contrast * 100 + 255)) / (255 * (259 - contrast * 100));
          let enhanced = factor * (gray - 128) + 128;
          enhanced = Math.max(0, Math.min(255, enhanced));
          
          data[i] = enhanced;
          data[i + 1] = enhanced;
          data[i + 2] = enhanced;
        }
        
        ctx.putImageData(imageData, 0, 0);
        
        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/png', 1.0);
        
        URL.revokeObjectURL(img.src);
      };
      
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  // Legacy preprocessing functions removed - using preprocessWhiteText and preprocessGrayscale instead

  // Convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix to get pure base64
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // AI-powered OCR using GPT-4o Vision
  const processWithAiOcr = async (file) => {
    const base64 = await fileToBase64(file);
    const mimeType = file.type || 'image/jpeg';
    
    const response = await fetch(`${API_URL}/api/ai-ocr`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image_base64: base64,
        mime_type: mimeType
      })
    });
    
    if (!response.ok) {
      throw new Error(`AI OCR request failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'AI OCR failed');
    }
    
    return data.text;
  };

  const processImages = async (files) => {
    setIsProcessing(true);
    setProgress(0);
    
    // Use Smart OCR mode if enabled
    if (useAiOcr) {
      setProgressText("Using Smart OCR (EasyOCR deep learning)...");
      const newImages = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgressText(`Smart OCR processing image ${i + 1} of ${files.length}...`);
        setProgress(Math.round(((i + 0.5) / files.length) * 100));
        
        try {
          const extractedText = await processWithAiOcr(file);
          
          newImages.push({
            id: Date.now() + i,
            name: file.name,
            file: file,
            preview: URL.createObjectURL(file),
            text: extractedText.trim(),
            confidence: 95, // EasyOCR is highly accurate
            words: extractedText.trim().split(/\s+/).filter(w => w.length > 0).length,
            method: 'smart-ocr'
          });
        } catch (error) {
          console.error(`Smart OCR error for ${file.name}:`, error);
          toast.error(`Smart OCR failed for ${file.name}: ${error.message}`);
        }
        
        setProgress(Math.round(((i + 1) / files.length) * 100));
      }
      
      setImages(prev => [...prev, ...newImages]);
      
      if (newImages.length > 0) {
        toast.success(`Extracted text from ${newImages.length} image${newImages.length > 1 ? 's' : ''}!`);
      }
      
      setIsProcessing(false);
      setProgress(0);
      setProgressText("");
      return;
    }

    // Standard Tesseract OCR
    setProgressText("Initializing OCR engine...");

    try {
      // Create worker with best quality settings
      const worker = await createWorker(language, 1, {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
            setProgressText(`Recognizing text: ${Math.round(m.progress * 100)}%`);
          } else if (m.status === 'loading language traineddata') {
            setProgressText('Loading language model...');
          } else {
            setProgressText(m.status);
          }
        }
      });
      workerRef.current = worker;

      // Set Tesseract parameters for better accuracy
      await worker.setParameters({
        tessedit_pageseg_mode: pageSegMode,
        tessedit_char_whitelist: '', // Allow all characters
        preserve_interword_spaces: '1',
      });

      const newImages = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgressText(`Processing image ${i + 1} of ${files.length}...`);
        
        try {
          // Collect results from different methods
          const results = [];
          
          // Helper to clean text - remove garbage characters
          const cleanText = (text) => {
            // Remove lines that are mostly non-alphanumeric
            const lines = text.split('\n');
            const cleanedLines = lines.filter(line => {
              const alphanumeric = line.replace(/[^a-zA-Z0-9]/g, '').length;
              const total = line.trim().length;
              // Keep line if >40% alphanumeric or it's empty (preserve structure)
              return total === 0 || (alphanumeric / total) > 0.4;
            });
            return cleanedLines.join('\n').trim();
          };
          
          // Method 1: Original image
          setProgressText(`Trying original image...`);
          const originalResult = await worker.recognize(file);
          results.push({
            text: cleanText(originalResult.data.text),
            confidence: originalResult.data.confidence,
            words: originalResult.data.words || [],
            method: 'original'
          });
          
          if (enhanceImage) {
            // Method 2: White text extraction (best for stylized text on colorful backgrounds)
            setProgressText(`Trying white text extraction...`);
            try {
              const whiteTextBlob = await preprocessWhiteText(file);
              const whiteTextResult = await worker.recognize(whiteTextBlob);
              results.push({
                text: cleanText(whiteTextResult.data.text),
                confidence: whiteTextResult.data.confidence,
                words: whiteTextResult.data.words || [],
                method: 'white-text'
              });
            } catch (e) { console.log('White text extraction failed:', e); }
            
            // Method 3: High-contrast grayscale (good for standard documents)
            setProgressText(`Trying grayscale enhancement...`);
            try {
              const grayscaleBlob = await preprocessGrayscale(file);
              const grayscaleResult = await worker.recognize(grayscaleBlob);
              results.push({
                text: cleanText(grayscaleResult.data.text),
                confidence: grayscaleResult.data.confidence,
                words: grayscaleResult.data.words || [],
                method: 'grayscale'
              });
            } catch (e) { console.log('Grayscale failed:', e); }
          }
          
          // Find best result based on CONFIDENCE primarily (not length)
          // Higher confidence = fewer garbage characters
          let bestResult = results[0];
          
          for (const result of results) {
            // Only consider results with actual readable text
            const hasReadableText = result.text.length > 5;
            const bestHasReadableText = bestResult.text.length > 5;
            
            if (!bestHasReadableText && hasReadableText) {
              bestResult = result;
            } else if (hasReadableText && result.confidence > bestResult.confidence) {
              bestResult = result;
            }
          }
          
          newImages.push({
            id: Date.now() + i,
            name: file.name,
            file: file,
            preview: URL.createObjectURL(file),
            text: bestResult.text.trim(),
            confidence: bestResult.confidence,
            words: bestResult.text.trim().split(/\s+/).filter(w => w.length > 0).length,
          });
        } catch (error) {
          console.error(`Error processing ${file.name}:`, error);
          toast.error(`Failed to process ${file.name}`);
        }
      }

      await worker.terminate();
      workerRef.current = null;

      setImages(prev => [...prev, ...newImages]);
      
      if (newImages.length > 0) {
        toast.success(`Extracted text from ${newImages.length} image${newImages.length > 1 ? 's' : ''}!`);
      }
    } catch (error) {
      console.error("OCR error:", error);
      toast.error("Failed to initialize OCR engine");
    }

    setIsProcessing(false);
    setProgress(0);
    setProgressText("");
  };

  const removeImage = (id) => {
    setImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) {
        URL.revokeObjectURL(img.preview);
      }
      return prev.filter(i => i.id !== id);
    });
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Text copied to clipboard!");
  };

  const copyAllText = () => {
    const allText = images.map(img => img.text).join('\n\n---\n\n');
    navigator.clipboard.writeText(allText);
    toast.success("All text copied to clipboard!");
  };

  const downloadText = (image) => {
    const blob = new Blob([image.text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = image.name.replace(/\.[^/.]+$/, '') + '.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadAllText = () => {
    const allText = images.map(img => `=== ${img.name} ===\n\n${img.text}`).join('\n\n---\n\n');
    const blob = new Blob([allText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `extracted-text-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearAll = () => {
    images.forEach(img => {
      URL.revokeObjectURL(img.preview);
    });
    setImages([]);
  };

  if (embedded) {
    return (
      <div className="p-4">
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400'
          }`}
        >
          <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <p className="text-lg font-medium">Drop images here or click to upload</p>
          <p className="text-sm text-slate-500 mt-2">Extract text using AI-powered OCR</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPT_TYPES}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KayTool
              </h1>
            </a>
            <div className="flex items-center gap-3">
              <ToolsNav />
              <Badge variant="secondary" className="hidden md:flex items-center gap-1 px-3 py-1 dark:bg-slate-700">
                <Info className="w-3 h-3" />
                AI-Powered OCR
              </Badge>
              <Button variant="outline" size="icon" onClick={() => setDarkMode(prev => !prev)} className="rounded-full">
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <Badge className="mb-4 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            Image to Text
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 dark:text-white mb-4">
            Extract Text from Images (OCR)
          </h2>
          <p className="text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            AI-powered optical character recognition with smart image enhancement. Extract text from photos, screenshots, scanned documents, and more.
          </p>
        </div>

        {/* Settings */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-4">
            {/* AI OCR Toggle - Featured */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <Label htmlFor="ai-ocr" className="font-semibold text-slate-900 dark:text-white cursor-pointer">
                    Smart OCR (Deep Learning)
                  </Label>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Free &amp; accurate - best for stylized text, decorative fonts, complex backgrounds
                  </p>
                </div>
              </div>
              <Switch
                id="ai-ocr"
                checked={useAiOcr}
                onCheckedChange={setUseAiOcr}
                disabled={isProcessing}
                className="data-[state=checked]:bg-purple-600"
              />
            </div>

            {/* Standard OCR Settings - shown when AI OCR is off */}
            {!useAiOcr && (
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Language Selection */}
                <div className="flex items-center gap-3">
                  <Languages className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  <span className="font-medium text-slate-700 dark:text-slate-300 text-sm">Language:</span>
                  <Select value={language} onValueChange={setLanguage} disabled={isProcessing}>
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>{lang.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="hidden lg:block w-px h-8 bg-slate-300 dark:bg-slate-600" />

                {/* Page Segmentation Mode */}
                <div className="flex items-center gap-3">
                  <Settings className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  <span className="font-medium text-slate-700 dark:text-slate-300 text-sm">Mode:</span>
                  <Select value={pageSegMode} onValueChange={setPageSegMode} disabled={isProcessing}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {pageSegModes.map(mode => (
                        <SelectItem key={mode.value} value={mode.value}>
                          <div>
                            <span>{mode.name}</span>
                            <span className="text-xs text-slate-500 ml-1">- {mode.desc}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="hidden lg:block w-px h-8 bg-slate-300 dark:bg-slate-600" />

                {/* Auto Enhancement */}
                <div className="flex items-center gap-3">
                  <Wand2 className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  <Label htmlFor="enhance" className="font-medium text-slate-700 dark:text-slate-300 text-sm cursor-pointer">
                    Auto Enhance
                  </Label>
                  <Switch
                    id="enhance"
                    checked={enhanceImage}
                    onCheckedChange={setEnhanceImage}
                    disabled={isProcessing}
                  />
                  <span className="text-xs text-slate-500">(Improves accuracy)</span>
                </div>
              </div>
            )}

            {/* AI OCR Info - shown when AI OCR is on */}
            {useAiOcr && (
              <div className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400">
                <Info className="w-4 h-4" />
                <span>Smart OCR uses deep learning for excellent accuracy on all text styles - 100% free</span>
              </div>
            )}

            {/* Standard OCR Warning - shown when AI OCR is off */}
            {!useAiOcr && (
              <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <Info className="w-4 h-4 flex-shrink-0" />
                <span><strong>Note:</strong> Standard OCR may struggle with stylized/decorative fonts. For quotes, memes, or artistic text, enable AI OCR for best results.</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Area */}
        <Card className="mb-6 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="p-6">
            <div
              onClick={() => !isProcessing && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
                isProcessing ? 'opacity-50 cursor-not-allowed' :
                isDragging 
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                  : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 dark:hover:border-blue-500'
              }`}
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-slate-400" />
              <p className="text-xl font-medium text-slate-700 dark:text-slate-300">
                Drop images here or click to upload
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                Supports PNG, JPG, WebP, BMP, GIF
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_TYPES}
              multiple
              onChange={handleFileSelect}
              className="hidden"
              disabled={isProcessing}
            />
          </CardContent>
        </Card>

        {/* Progress */}
        {isProcessing && (
          <Card className="mb-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{progressText || "Processing..."}</span>
                <span className="text-sm text-slate-500">{progress}%</span>
              </div>
              <Progress value={progress} className={`h-2 ${useAiOcr ? '[&>div]:bg-purple-600' : ''}`} />
              <p className="text-xs text-slate-500 mt-2">
                {useAiOcr 
                  ? "Using Smart OCR (deep learning) for best accuracy on stylized text" 
                  : enhanceImage 
                    ? "Auto-enhancement enabled - trying multiple processing methods for best results" 
                    : "First-time processing may take longer as the OCR model downloads (~15MB)"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {images.length > 0 && (
          <>
            {/* Ad Placement - After Results Header (Responsive) */}
            <AdContainer className="mb-4">
              <ResponsiveAd slot="after-upload" mobileSize="mobile-banner" desktopSize="banner" />
            </AdContainer>

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                Extracted Text ({images.length} image{images.length > 1 ? 's' : ''})
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" onClick={clearAll} className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Clear All
                </Button>
                <Button variant="outline" onClick={copyAllText} className="gap-2">
                  <Copy className="w-4 h-4" /> Copy All
                </Button>
                <Button onClick={downloadAllText} className="gap-2 bg-blue-600 hover:bg-blue-700">
                  <Download className="w-4 h-4" /> Download All
                </Button>
              </div>
            </div>

            {/* Ad Placement - Above Results (336×280 - Best Money Position) */}
            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-4" />

            <div className="space-y-4">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden dark:bg-slate-800">
                  <CardContent className="p-0">
                    <div className="flex flex-col lg:flex-row">
                      {/* Image Preview */}
                      <div className="lg:w-1/3 bg-slate-100 dark:bg-slate-700 p-4">
                        <div className="relative">
                          <img
                            src={image.preview}
                            alt={image.name}
                            className="w-full h-auto max-h-[300px] object-contain rounded-lg"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-2 right-2 w-8 h-8"
                            onClick={() => removeImage(image.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="mt-3 text-sm text-slate-600 dark:text-slate-400">
                          <p className="font-medium truncate">{image.name}</p>
                          <p className="text-xs mt-1 flex items-center gap-2">
                            {image.method === 'smart-ocr' && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium">
                                <Sparkles className="w-3 h-3" /> Smart
                              </span>
                            )}
                            <span>
                              Confidence: <span className={image.confidence > 80 ? 'text-green-600' : image.confidence > 60 ? 'text-yellow-600' : 'text-red-600'}>{Math.round(image.confidence)}%</span>
                            </span>
                            <span>• {image.words} words</span>
                          </p>
                        </div>
                      </div>
                      
                      {/* Extracted Text */}
                      <div className="lg:w-2/3 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Extracted Text</span>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => copyText(image.text)} className="gap-1">
                              <Copy className="w-3 h-3" /> Copy
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => downloadText(image)} className="gap-1">
                              <Download className="w-3 h-3" /> Download
                            </Button>
                          </div>
                        </div>
                        <textarea
                          value={image.text}
                          readOnly
                          className="w-full h-[250px] p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded-lg text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Ad Placement - Before Features (Responsive) */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="before-features" mobileSize="mobile-rectangle" desktopSize="rectangle" />
        </AdContainer>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {[
            { title: "Smart OCR", desc: "Deep learning powered OCR for perfect accuracy on stylized fonts, decorative text, and complex backgrounds - 100% free", highlight: true },
            { title: "Smart Enhancement", desc: "Intelligent text extraction that isolates white/light text from colorful backgrounds" },
            { title: "100% Private", desc: "All processing happens on our secure servers - no data stored after processing" }
          ].map((feature, i) => (
            <Card key={i} className={`dark:bg-slate-800 ${feature.highlight ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20' : ''}`}>
              <CardContent className="p-6 text-center">
                <h4 className={`font-semibold mb-2 ${feature.highlight ? 'text-purple-700 dark:text-purple-300' : 'dark:text-white'}`}>{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tips */}
        <Card className="mt-6 dark:bg-slate-800 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
          <CardContent className="p-4">
            <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Tips for better results:</h4>
            <ul className="text-sm text-blue-600 dark:text-blue-300 space-y-1">
              <li>• <strong>Smart OCR (recommended)</strong> - best for stylized text, quotes, memes, decorative fonts</li>
              <li>• Standard OCR is faster for simple documents, screenshots, receipts</li>
              <li>• Both options are completely free to use</li>
              <li>• Higher resolution images generally produce better results</li>
            </ul>
          </CardContent>
        </Card>

        {/* Ad Placement - After Tips (Before Footer) - Responsive */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="before-footer" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content */}
      {pageContent?.content && (
        <div className="bg-white dark:bg-slate-800 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={pageContent.content} />
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad - Fixed at bottom on mobile only */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}

export default ImageToText;
